class BannerSlider extends HTMLElement {
  constructor() {
    super();
    this.slider = this.querySelector('.banner-slider__wrapper');
    this.slides = this.querySelectorAll('.banner-slider__slide');
    this.prevButton = this.querySelector('.banner-slider__button--prev');
    this.nextButton = this.querySelector('.banner-slider__button--next');
    this.forms = this.querySelectorAll('.banner-slider__form');
    this.variantRadios = this.querySelectorAll('.banner-slider__option input[type="radio"]');
    this.imageNavPrevButtons = this.querySelectorAll('.banner-slider__image-nav--prev');
    this.imageNavNextButtons = this.querySelectorAll('.banner-slider__image-nav--next');
    this.currentIndex = 0;
    this.startX = 0;
    this.currentTranslate = 0;
    this.prevTranslate = 0;
    this.isDragging = false;

    this.init();
  }

  init() {
    this.prevButton.addEventListener('click', () => this.slide('prev'));
    this.nextButton.addEventListener('click', () => this.slide('next'));
    this.updateButtons();

    // Handle form submissions
    this.forms.forEach((form) => {
      form.addEventListener('submit', this.handleFormSubmit.bind(this));
    });

    // Handle variant selection
    this.variantRadios.forEach((radio) => {
      radio.addEventListener('change', this.handleVariantChange.bind(this));
    });

    // Handle image navigation
    this.imageNavPrevButtons.forEach((button) => {
      button.addEventListener('click', this.navigateProductImage.bind(this, 'prev'));
    });

    this.imageNavNextButtons.forEach((button) => {
      button.addEventListener('click', this.navigateProductImage.bind(this, 'next'));
    });

    // Touch events
    this.slider.addEventListener('touchstart', this.touchStart.bind(this));
    this.slider.addEventListener('touchmove', this.touchMove.bind(this));
    this.slider.addEventListener('touchend', this.touchEnd.bind(this));
  }

  handleVariantChange(event) {
    const radio = event.target;
    const slide = radio.closest('.banner-slider__slide');
    const form = document.getElementById(radio.getAttribute('form'));
    if (!form) return;

    const productVariantId = form.querySelector('.product-variant-id');
    const priceElement = slide.querySelector('.banner-slider__price');
    const addToCartButton = form.querySelector('button[type="submit"]');

    // Show loading state
    if (addToCartButton) {
      const spinner = addToCartButton.querySelector('.loading__spinner');
      if (spinner) spinner.classList.remove('hidden');
      addToCartButton.classList.add('loading');
      addToCartButton.setAttribute('aria-disabled', true);
    }

    // Get all selected options
    const selectedOptions = [];
    const optionRadios = slide.querySelectorAll('.banner-slider__option input[type="radio"]:checked');
    optionRadios.forEach((radio) => {
      const optionPosition = radio.name.replace('option-', '');
      selectedOptions[parseInt(optionPosition) - 1] = radio.value;
    });

    // Try to get the product URL from the "View More" link
    const viewMoreLink = slide.querySelector('.banner-slider__view-more-button');
    let productUrl;

    if (viewMoreLink) {
      productUrl = viewMoreLink.getAttribute('href');
    } else {
      // Fallback to using the product handle data attribute
      const productHandle = form.dataset.productHandle || slide.dataset.productHandle;
      if (!productHandle) {
        console.error('Could not find product handle');
        return;
      }
      productUrl = `/products/${productHandle}`;
    }

    // Now fetch the product data using the product URL
    fetch(productUrl + '.js')
      .then((response) => {
        if (!response.ok) {
          throw new Error('Network response was not ok: ' + response.statusText);
        }
        return response.json();
      })
      .then((productData) => {
        // Find the matching variant
        const matchingVariant = productData.variants.find((variant) => {
          return variant.options.every((option, index) => {
            return option === selectedOptions[index];
          });
        });

        if (matchingVariant) {
          // Update the variant ID in the form
          productVariantId.value = matchingVariant.id;

          // Update the price display
          if (priceElement) {
            priceElement.innerHTML = formatMoney(matchingVariant.price);
          }

          // Check if variant is available and update the add to cart button
          if (addToCartButton) {
            if (matchingVariant.available) {
              addToCartButton.classList.remove('sold-out');
              addToCartButton.removeAttribute('disabled');
              addToCartButton.querySelector('span').textContent = window.variantStrings?.addToCart || 'Add to cart';
            } else {
              addToCartButton.classList.add('sold-out');
              addToCartButton.setAttribute('disabled', 'disabled');
              addToCartButton.querySelector('span').textContent = window.variantStrings?.soldOut || 'Sold out';
            }
          }

          // Update the image if the variant has a specific image
          if (matchingVariant.featured_image) {
            const imageWrapper = slide.querySelector('.banner-slider__image-wrapper');
            if (imageWrapper) {
              const images = imageWrapper.querySelectorAll('.banner-slider__img');

              // First try to find an existing image that matches the variant image
              let foundMatchingImage = false;
              images.forEach((image) => {
                if (image.src.includes(matchingVariant.featured_image.src)) {
                  // Remove active class from all images
                  images.forEach((img) => img.classList.remove('active'));
                  // Add active class to matching image
                  image.classList.add('active');
                  foundMatchingImage = true;
                }
              });

              // If no matching image was found, update the first image
              if (!foundMatchingImage && images.length > 0) {
                const firstImage = images[0];

                // Update the image while preserving aspect ratio
                firstImage.src = matchingVariant.featured_image.src;
                firstImage.srcset = matchingVariant.featured_image.srcset || matchingVariant.featured_image.src;

                // Make sure it's active
                images.forEach((img) => img.classList.remove('active'));
                firstImage.classList.add('active');
              }
            }
          }

          // Add variant name to a data attribute for UI reference if needed
          form.setAttribute('data-variant-name', matchingVariant.title);
        } else {
          // No matching variant found - handle unavailable combination
          if (addToCartButton) {
            addToCartButton.classList.add('sold-out');
            addToCartButton.setAttribute('disabled', 'disabled');
            addToCartButton.querySelector('span').textContent = window.variantStrings?.unavailable || 'Unavailable';
          }

          if (priceElement) {
            priceElement.innerHTML = window.variantStrings?.unavailable || 'Unavailable';
          }
        }
      })
      .catch((error) => {
        console.error('Error fetching product data:', error);
        // Reset the form state on error
        if (addToCartButton) {
          addToCartButton.classList.add('error');
          addToCartButton.setAttribute('disabled', 'disabled');
          addToCartButton.querySelector('span').textContent = 'Error loading variant';
        }
      })
      .finally(() => {
        // Hide loading state
        if (addToCartButton) {
          const spinner = addToCartButton.querySelector('.loading__spinner');
          if (spinner) spinner.classList.add('hidden');
          addToCartButton.classList.remove('loading');
          addToCartButton.removeAttribute('aria-disabled');
        }
      });
  }

  handleFormSubmit(event) {
    event.preventDefault();
    const form = event.target;
    const submitButton = form.querySelector('button[type="submit"]');
    const spinner = submitButton.querySelector('.loading__spinner');

    submitButton.classList.add('loading');
    submitButton.setAttribute('aria-disabled', true);
    spinner.classList.remove('hidden');

    // Prepare the fetch configuration
    const config = {
      method: 'POST',
      headers: {
        'X-Requested-With': 'XMLHttpRequest',
        Accept: 'application/json',
      },
      body: new FormData(form),
    };

    // Get the cart drawer or notification if available
    const cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');

    if (cart) {
      const formData = new FormData(form);
      formData.append(
        'sections',
        cart.getSectionsToRender().map((section) => section.id)
      );
      formData.append('sections_url', window.location.pathname);
      config.body = formData;
    }

    // Submit the form via AJAX
    fetch(form.action, config)
      .then((response) => response.json())
      .then((response) => {
        if (response.status) {
          // Handle error
          console.error(response.description);
          const errorMessage = response.description || 'Could not add item to cart.';

          // If there's an event system for displaying errors, use it
          if (window.PUB_SUB_EVENTS && window.publish) {
            publish(window.PUB_SUB_EVENTS.cartError, {
              source: 'banner-slider',
              productVariantId: form.querySelector('[name="id"]').value,
              errors: response.errors || response.description,
              message: errorMessage,
            });
          } else {
            alert(errorMessage);
          }
        } else if (cart) {
          // Update cart drawer/notification
          cart.renderContents(response);
        } else {
          // Fallback to redirect if no cart drawer
          window.location = window.routes.cart_url;
        }
      })
      .catch((error) => {
        console.error('Error:', error);
      })
      .finally(() => {
        submitButton.classList.remove('loading');
        submitButton.removeAttribute('aria-disabled');
        spinner.classList.add('hidden');
      });
  }

  touchStart(event) {
    this.startX = this.getPositionX(event);
    this.isDragging = true;
    this.slider.style.transition = 'none';
  }

  touchMove(event) {
    if (!this.isDragging) return;

    const currentPosition = this.getPositionX(event);
    this.currentTranslate = this.prevTranslate + currentPosition - this.startX;

    // Prevent scrolling past the first and last slides
    const maxTranslate = 0;
    const minTranslate = -(this.slides.length - 1) * 100;

    if (this.currentTranslate > maxTranslate) {
      this.currentTranslate = maxTranslate;
    } else if (this.currentTranslate < minTranslate) {
      this.currentTranslate = minTranslate;
    }

    this.slider.style.transform = `translateX(${this.currentTranslate}%)`;
  }

  touchEnd() {
    this.isDragging = false;
    this.slider.style.transition = 'transform 0.5s ease';

    // Snap to the nearest slide
    const movedBy = this.currentTranslate - this.prevTranslate;
    const threshold = 30; // Minimum distance to trigger slide change

    if (Math.abs(movedBy) > threshold) {
      if (movedBy < 0) {
        this.slide('next');
      } else {
        this.slide('prev');
      }
    } else {
      this.slideTo(this.currentIndex);
    }
  }

  getPositionX(event) {
    return event.type.includes('mouse') ? event.pageX : event.touches[0].clientX;
  }

  slide(direction) {
    if (direction === 'next') {
      this.currentIndex = (this.currentIndex + 1) % this.slides.length;
    } else {
      this.currentIndex = (this.currentIndex - 1 + this.slides.length) % this.slides.length;
    }

    this.slideTo(this.currentIndex);
  }

  slideTo(index) {
    this.currentIndex = index;
    this.prevTranslate = -index * 100;
    this.slider.style.transform = `translateX(${this.prevTranslate}%)`;
    this.updateButtons();
    this.updateAriaHidden();
  }

  updateButtons() {
    this.prevButton.disabled = this.currentIndex === 0;
    this.nextButton.disabled = this.currentIndex === this.slides.length - 1;
  }

  updateAriaHidden() {
    this.slides.forEach((slide, index) => {
      slide.setAttribute('aria-hidden', index !== this.currentIndex);
    });
  }

  navigateProductImage(direction, event) {
    const button = event.currentTarget;
    const slide = button.closest('.banner-slider__slide');
    const images = slide.querySelectorAll('.banner-slider__img');

    if (images.length <= 1) return;

    // Find the currently active image
    let activeIndex = 0;
    images.forEach((image, index) => {
      if (image.classList.contains('active')) {
        activeIndex = index;
        image.classList.remove('active'); // Remove active class right away for smoother transition
      }
    });

    // Calculate the new index
    let newIndex;
    if (direction === 'next') {
      newIndex = (activeIndex + 1) % images.length;
    } else {
      newIndex = (activeIndex - 1 + images.length) % images.length;
    }

    // Update active class
    images[newIndex].classList.add('active');

    // Prevent default behavior
    event.preventDefault();
    event.stopPropagation();
  }
}

customElements.define('banner-slider', BannerSlider);
